/**
 * NodeReadService.js
 *
 * Central place for Firestore node reads, queries, and subscriptions.
 * This module is intentionally separate from NodeWriteService.
 */

// Firebase
import { collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';
import { listAdjacentNodeKeys, subscribeAdjacentNodeKeys } from './NodeEdgeService';
import { NodeVisibility, filterNodesByVisibility, isNodeTrashed } from './NodeTrash';

// Utilities
import { timestampToMillis } from 'utilities/DateTime';
import { canUser, PermissionAction } from 'utilities/NodeSecurity';

// ---- Key invariants ----
// Invariant: Firestore document id is the canonical node key.
// We keep warnings lightweight (warn once per doc id).
const _warnedKeyMismatches = new Set();

const warnIfStoredKeyMismatch = (docId, storedKey) => {
	if (!storedKey || storedKey === docId) return;
	if (_warnedKeyMismatches.has(docId)) return;
	_warnedKeyMismatches.add(docId);
	console.warn(`[NodeReadService] key mismatch for doc ${docId}: stored key=${storedKey}`);
};

const buildNodeFromDoc = (docSnap) => {
	const data = docSnap.data() || {};
	warnIfStoredKeyMismatch(docSnap.id, data?.key);
	return { id: docSnap.id, ...data, key: docSnap.id };
};

const normalizeTagKey = (tag) => String(tag || '').trim().toLowerCase();

const normalizeVisibility = (visibility = NodeVisibility.ACTIVE) => {
	const resolved = String(visibility || NodeVisibility.ACTIVE).trim().toLowerCase();
	if (resolved === NodeVisibility.ALL) return NodeVisibility.ALL;
	if (resolved === NodeVisibility.TRASHED) return NodeVisibility.TRASHED;
	return NodeVisibility.ACTIVE;
};

const applyVisibilityToNode = (node, visibility = NodeVisibility.ACTIVE) => {
	if (!node) return null;
	const resolvedVisibility = normalizeVisibility(visibility);
	const trashed = isNodeTrashed(node);

	if (resolvedVisibility === NodeVisibility.ALL) return node;
	if (resolvedVisibility === NodeVisibility.TRASHED) {
		return trashed ? node : null;
	}

	return trashed ? null : node;
};

const applyVisibilityToRows = (rows, visibility = NodeVisibility.ACTIVE) => {
	return filterNodesByVisibility(rows, normalizeVisibility(visibility));
};

/**
 * Utility that merges a base node with its secured payload.
 *
 * @param {object} baseNode - The base node document.
 * @param {object} securedPayload - The secured payload document.
 * 
 * @returns {object} The merged node document.
 */
const mergeNodeWithSecured = (baseNode, securedPayload) => {

	// Merge secured payload into base node, preferring secured values
	if (!baseNode || !securedPayload) return baseNode;

	// Deep merge logic for nested objects
	const merged = { ...baseNode };

	// Merge each section from secured payload into the merged node
	Object.keys(securedPayload).forEach((sectionKey) => {

		// Skip if no value in secured payload
		const securedValue = securedPayload[sectionKey];

		// Base value from the original node
		const baseValue = merged[sectionKey];

		// If both are objects, merge their properties
		if (securedValue &&
			typeof securedValue === 'object' &&
			!Array.isArray(securedValue)
		) {
			if (baseValue && typeof baseValue === 'object' && !Array.isArray(baseValue)) {
				merged[sectionKey] = { ...baseValue, ...securedValue };
			} else {
				merged[sectionKey] = { ...securedValue };
			}
		} else {
			merged[sectionKey] = securedValue;
		}
	});

	return merged;
};

/**
 * Fetches a single node by key, merging in secured payload if available.
 * 
 * @param {string} key - The unique key of the node to fetch.
 * 
 * @returns {Promise<object|null>} The node object or null if not found.
 */
export const getNode = async (key, options = {}) => {
	const { visibility = NodeVisibility.ACTIVE } = options;

	// Fetch base node document
	const nodeRef = doc(db, collections.nodes, key);

	// Get base node snapshot
	const snap = await getDoc(nodeRef);

	// If node doesn't exist, return null
	if (!snap.exists()) return null;

	// Base node data
	const snapData = snap.data() || {};
	warnIfStoredKeyMismatch(snap.id, snapData?.key);

	// Construct base node object (doc id is canonical)
	const baseNode = { id: snap.id, ...snapData, key: snap.id };

	// Attempt to load secured payload; if forbidden or missing, fall back to base.
	try {
		// Fetch secured payload document
		const securedRef = doc(db, collections.nodes, key, 'secured', 'main');

		// Get secured snapshot
		const securedSnap = await getDoc(securedRef);

		// If secured exists, merge it
		if (securedSnap.exists()) {
			const securedPayload = securedSnap.data() || {};
			return applyVisibilityToNode(mergeNodeWithSecured(baseNode, securedPayload), visibility);
		}
	} catch (err) {
		console.warn('[NodeReadService] getNode secured read error:', err);
	}

	return applyVisibilityToNode(baseNode, visibility);
};

/**
 * Fetch multiple nodes by key, returning any readable nodes.
 *
 * @param {string[]} nodeKeys - Array of node keys.
 * @returns {Promise<object[]>} Array of node objects.
 */
export const getNodesByKeys = async (nodeKeys = [], options = {}) => {
	const { visibility = NodeVisibility.ACTIVE } = options;
	if (!Array.isArray(nodeKeys) || nodeKeys.length === 0) return [];

	const keys = Array.from(
		new Set(
			nodeKeys
				.filter((key) => typeof key === 'string' && key.trim())
				.map((key) => key.trim())
		)
	);

	if (keys.length === 0) return [];

	const chunks = [];
	for (let i = 0; i < keys.length; i += 10) {
		chunks.push(keys.slice(i, i + 10));
	}

	const baseSnapshots = await Promise.all(
		chunks.map((chunk) => {
			const q = query(
				collection(db, collections.nodes),
				where('__name__', 'in', chunk)
			);
			return getDocs(q);
		})
	);

	const baseByKey = new Map();
	baseSnapshots.forEach((snapshot) => {
		snapshot.docs.forEach((docSnap) => {
			baseByKey.set(docSnap.id, buildNodeFromDoc(docSnap));
		});
	});

	const securedByKey = new Map();
	await Promise.all(
		keys.map(async (key) => {
			if (!baseByKey.has(key)) return;

			try {
				const securedRef = doc(db, collections.nodes, key, 'secured', 'main');
				const securedSnap = await getDoc(securedRef);
				if (!securedSnap.exists()) return;
				securedByKey.set(key, securedSnap.data() || {});
			} catch (error) {
				console.warn('[NodeReadService] getNodesByKeys secured read error:', error);
			}
		})
	);

	return applyVisibilityToRows(keys
		.map((key) => {
			const base = baseByKey.get(key);
			if (!base) return null;

			const secured = securedByKey.get(key);
			return secured ? mergeNodeWithSecured(base, secured) : base;
		})
		.filter(Boolean), visibility);
};

/**
 * Fetches popular tags used by other nodes of the same type in a workspace.
 *
 * @param {object} params - Query parameters.
 * @param {string} params.workspaceKey - Workspace key.
 * @param {string} params.typeKey - Node type key.
 * @param {string} [params.excludeNodeKey] - Optional current node key to exclude.
 * @param {number} [params.maxNodes=80] - Maximum nodes to inspect.
 * @param {number} [params.maxTags=20] - Maximum suggestions to return.
 *
 * @returns {Promise<Array<{ value: string, count: number }>>} Ranked tag suggestions.
 */
export const getNodeTypeTagSuggestions = async ({
	workspaceKey,
	typeKey,
	excludeNodeKey,
	maxNodes = 80,
	maxTags = 20,
	visibility = NodeVisibility.ACTIVE,
} = {}) => {
	const cleanWorkspaceKey = String(workspaceKey || '').trim();
	const cleanTypeKey = String(typeKey || '').trim();
	const cleanExcludeNodeKey = String(excludeNodeKey || '').trim();

	if (!cleanWorkspaceKey || !cleanTypeKey) return [];

	const suggestionsSnapshot = await getDocs(query(
		collection(db, collections.nodes),
		where('workspaceKey', '==', cleanWorkspaceKey),
		where('type', '==', cleanTypeKey),
		limit(Math.max(1, Number(maxNodes) || 80))
	));

	const tagCounts = new Map();

	suggestionsSnapshot.docs.forEach((docSnap) => {
		if (cleanExcludeNodeKey && docSnap.id === cleanExcludeNodeKey) {
			return;
		}

		const node = buildNodeFromDoc(docSnap);
		if (!applyVisibilityToNode(node, visibility)) {
			return;
		}
		const nodeTags = Array.isArray(node?.tags) ? node.tags : [];

		nodeTags.forEach((rawTag) => {
			const cleanTag = String(rawTag || '').trim();
			const normalizedTag = normalizeTagKey(cleanTag);

			if (!normalizedTag) {
				return;
			}

			const existing = tagCounts.get(normalizedTag) || {
				count: 0,
				variants: new Map(),
			};

			existing.count += 1;
			existing.variants.set(cleanTag, (existing.variants.get(cleanTag) || 0) + 1);
			tagCounts.set(normalizedTag, existing);
		});
	});

	return Array.from(tagCounts.entries())
		.map(([normalizedTag, value]) => {
			const variants = Array.from(value.variants.entries())
				.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }));

			return {
				normalizedTag,
				value: variants[0]?.[0] || normalizedTag,
				count: value.count,
			};
		})
		.sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }))
		.slice(0, Math.max(1, Number(maxTags) || 20))
		.map(({ value, count }) => ({ value, count }));
};

/**
 * Fetch minimal fields for a list of node keys.
 * Intended for breadcrumb hydration without pulling secured payload.
 * 
 * @param {string[]} nodeKeys - Array of node keys.
 * 
 * @returns {Promise<Map<string, { title: string, type: string }>>}
 *          Map of nodeKey to minimal fields.
 */
export const getNodeBreadcrumbFields = async (nodeKeys) => {

	// Validate input
	if (!Array.isArray(nodeKeys) || nodeKeys.length === 0) return new Map();

	// Normalize keys and remove duplicates
	const keys = Array.from(
		new Set(
			nodeKeys
				.filter((key) => typeof key === 'string' && key.trim())
				.map((key) => key.trim())
		)
	);
	// Early return if no valid keys
	if (keys.length === 0) return new Map();

	// Fetch node documents
	const refs = keys.map((key) => doc(db, collections.nodes, key));

	// Get all snapshots in parallel
	const snaps = await Promise.all(refs.map((ref) => getDoc(ref)));

	// Construct result map
	const result = new Map();

	// Populate map with minimal fields
	snaps.forEach((snap) => {
		if (!snap.exists()) return;
		const data = snap.data() || {};
		result.set(snap.id, {
			title: data.title || '',
			type: data.type || 'NODE',
		});
	});

	// Return the map of minimal fields
	return result;
};

/**
 * Real-time listener for a single node.
 * 
 * @param {string} key - The unique key of the node to subscribe to.
 * @param {function} callback - Receives updated node data
 * 
 * @returns {function} unsubscribe
 *
 * Usage:
 * const unsubscribe = subscribeToNode('abc123', (node) => { ... });
 */
export const subscribeToNode = (key, callback, options = {}) => {
	const { visibility = NodeVisibility.ACTIVE } = options;

	// References to base and secured documents
	const nodeRef = doc(db, collections.nodes, key);

	// Secured payload reference
	const securedRef = doc(db, collections.nodes, key, 'secured', 'main');

	// Latest snapshots
	let latestBase = null;
	let latestSecured = null;

	// Emit merged node to callback
	const emit = () => {

		// If no base node, emit null
		if (!latestBase) {
			callback(null);
			return;
		}

		// Merge base and secured payload
		const merged = applyVisibilityToNode(mergeNodeWithSecured(latestBase, latestSecured), visibility);

		// Emit merged node
		callback(merged);
	};

	// Subscribe to base node
	const unsubscribeBase = onSnapshot(
		nodeRef,
		(snap) => {
			if (snap.exists()) {
				const snapData = snap.data() || {};
				warnIfStoredKeyMismatch(snap.id, snapData?.key);
				latestBase = { id: snap.id, ...snapData, key: snap.id };
			} else {
				latestBase = null;
			}
			emit();
		},
		(error) => {
			console.error('[NodeReadService] subscribeToNode base error:', error);
			latestBase = null;
			emit();
		}
	);

	// Subscribe to secured payload
	const unsubscribeSecured = onSnapshot(
		securedRef,
		(snap) => {
			latestSecured = snap.exists() ? (snap.data() || {}) : null;
			emit();
		},
		(error) => {
			// Permission errors or missing secured docs should not break base node subscription.
			console.warn('[NodeReadService] subscribeToNode secured error:', error);
			latestSecured = null;
			emit();
		}
	);

	// Return unsubscribe function
	return () => {
		unsubscribeBase();
		unsubscribeSecured();
	};
};

/**
 * Lists all top-level nodes (those without a parent).
 * 
 * @returns {Promise<object[]>}
 *
 * Usage:
 * const roots = await listRootNodes();
 */
export const listRootNodes = async ({ visibility = NodeVisibility.ACTIVE } = {}) => {

	// Query for top-level profile-root nodes
	const q = query(collection(db, collections.nodes), where('profileRoot', '==', true));

	// Execute query
	const snapshot = await getDocs(q);

	// Map documents to node objects
	return applyVisibilityToRows(snapshot.docs.map(buildNodeFromDoc), visibility);
};

/**
 * Real-time subscription to top-level nodes.
 * 
 * @param {function} callback - Receives an array of node objects
 * 
 * @returns {function} unsubscribe
 *
 * Usage:
 * const unsubscribe = subscribeToRootNodes((nodes) => { ... });
 */
export const subscribeToRootNodes = (callback, { visibility = NodeVisibility.ACTIVE } = {}) => {

	// Query for top-level profile-root nodes
	const q = query(collection(db, collections.nodes), where('profileRoot', '==', true));

	// Subscribe to query
	return onSnapshot(q, (snapshot) => {

		// Map documents to node objects
		const nodes = applyVisibilityToRows(snapshot.docs.map(buildNodeFromDoc), visibility);

		// Invoke callback with nodes
		callback(nodes);
	});
};

/**
 * Lists all children for a given node.
 * 
 * @param {string} nodeKey - The node key.
 * @param {{ includeSecured?: boolean }} [options] - Optional read mode.
 * 
 * @returns {Promise<object[]>} - Array of child node objects.
 *
 * Usage:
 * const children = await listChildNodes('abc123');
 */
export const listChildNodes = async (nodeKey, { includeSecured = true, visibility = NodeVisibility.ACTIVE, workspaceKey = null } = {}) => {
	if (!nodeKey) return [];

	const childKeys = await listAdjacentNodeKeys(nodeKey, { workspaceKey });
	if (childKeys.length === 0) return [];

	const children = includeSecured
		? await getNodesByKeys(childKeys, { visibility })
		: await listNodesByKeys(childKeys, { visibility });
	return children.filter(Boolean);
};

/**
 * Lists child nodes for multiple parent keys in a single batched pass.
 *
 * @param {string[]} parentKeys - Parent node keys.
 * @param {{ includeSecured?: boolean }} [options] - Optional read mode.
 *
 * @returns {Promise<Record<string, object[]>>} - Map of parent key to child node rows.
 */
export const listChildNodesByParentKeys = async (parentKeys = [], { includeSecured = true, visibility = NodeVisibility.ACTIVE, workspaceKey = null } = {}) => {
	const normalizedParentKeys = Array.from(
		new Set(
			(Array.isArray(parentKeys) ? parentKeys : [])
				.filter((key) => typeof key === 'string' && key.trim())
				.map((key) => key.trim())
		)
	);

	if (!normalizedParentKeys.length) return {};

	const parentToChildSet = new Map(
		normalizedParentKeys.map((key) => [key, new Set()])
	);

	const chunks = [];
	for (let i = 0; i < normalizedParentKeys.length; i += 10) {
		chunks.push(normalizedParentKeys.slice(i, i + 10));
	}

	const edgeDatabase = collection(db, collections.edges);
	const normalizedWorkspaceKey = typeof workspaceKey === 'string' && workspaceKey.trim()
		? workspaceKey.trim()
		: null;

	const [sourceSnapshots, targetSnapshots] = await Promise.all([
		Promise.all(
			chunks.map((chunk) => {
				if (normalizedWorkspaceKey) {
					return getDocs(query(
						edgeDatabase,
						where('sourceNodeKey', 'in', chunk),
						where('workspaceKey', '==', normalizedWorkspaceKey)
					));
				}

				return getDocs(query(edgeDatabase, where('sourceNodeKey', 'in', chunk)));
			})
		),
		Promise.all(
			chunks.map((chunk) => {
				if (normalizedWorkspaceKey) {
					return getDocs(query(
						edgeDatabase,
						where('targetNodeKey', 'in', chunk),
						where('workspaceKey', '==', normalizedWorkspaceKey)
					));
				}

				return getDocs(query(edgeDatabase, where('targetNodeKey', 'in', chunk)));
			})
		),
	]);

	sourceSnapshots.forEach((snapshot) => {
		snapshot.docs.forEach((docSnap) => {
			const edge = docSnap.data() || {};
			const parentKey = typeof edge?.sourceNodeKey === 'string' ? edge.sourceNodeKey.trim() : '';
			const childKey = typeof edge?.targetNodeKey === 'string' ? edge.targetNodeKey.trim() : '';
			if (!parentKey || !childKey) return;
			const children = parentToChildSet.get(parentKey);
			if (!children) return;
			children.add(childKey);
		});
	});

	targetSnapshots.forEach((snapshot) => {
		snapshot.docs.forEach((docSnap) => {
			const edge = docSnap.data() || {};
			const parentKey = typeof edge?.targetNodeKey === 'string' ? edge.targetNodeKey.trim() : '';
			const childKey = typeof edge?.sourceNodeKey === 'string' ? edge.sourceNodeKey.trim() : '';
			if (!parentKey || !childKey) return;
			const children = parentToChildSet.get(parentKey);
			if (!children) return;
			children.add(childKey);
		});
	});

	const allChildKeys = Array.from(
		new Set(
			Array.from(parentToChildSet.values())
				.flatMap((childSet) => Array.from(childSet))
		)
	);

	const childRows = includeSecured
		? await getNodesByKeys(allChildKeys, { visibility })
		: await listNodesByKeys(allChildKeys, { visibility });

	const childByKey = new Map();
	childRows.forEach((row) => {
		if (!row?.key) return;
		childByKey.set(row.key, row);
	});

	const result = {};
	normalizedParentKeys.forEach((parentKey) => {
		const childSet = parentToChildSet.get(parentKey) || new Set();
		result[parentKey] = Array.from(childSet)
			.map((childKey) => childByKey.get(childKey) || null)
			.filter(Boolean);
	});

	return result;
};

/**
 * subscribeToChildNodes
 *
 * Subscribes to child nodes of a given parent and emits an array of children the user can view.
 *
 * @param {string} nodeKey - The node key
 * @param {object} user - Current user object
 * @param {object} parentNode - Parent node object
 * @param {function} callback - Receives an array of child nodes
 * 
 * @returns {function} unsubscribe
 *
 * Usage:
 * const unsubscribe = subscribeToChildNodes('abc123', user, parentNode, (children) => { ... });
 */
export const subscribeToChildNodes = (nodeKey, user, parentNode, callback) => {
	if (!nodeKey || typeof callback !== 'function') {
		return () => {};
	}

	const normalizedWorkspaceKey = typeof parentNode?.workspaceKey === 'string' && parentNode.workspaceKey.trim()
		? parentNode.workspaceKey.trim()
		: null;
	const sourceQuery = normalizedWorkspaceKey
		? query(
			collection(db, collections.edges),
			where('sourceNodeKey', '==', nodeKey),
			where('workspaceKey', '==', normalizedWorkspaceKey)
		)
		: query(collection(db, collections.edges), where('sourceNodeKey', '==', nodeKey));
	const targetQuery = normalizedWorkspaceKey
		? query(
			collection(db, collections.edges),
			where('targetNodeKey', '==', nodeKey),
			where('workspaceKey', '==', normalizedWorkspaceKey)
		)
		: query(collection(db, collections.edges), where('targetNodeKey', '==', nodeKey));

	let sourceRows = [];
	let targetRows = [];
	let orderedChildKeys = [];
	const childNodeByKey = new Map();
	const childUnsubscribeByKey = new Map();
	const hydratedChildKeySet = new Set();

	const emitFromCache = () => {
		if (orderedChildKeys.length === 0) {
			callback([]);
			return;
		}

		const hasPendingInitialHydration = orderedChildKeys.some((childKey) => !hydratedChildKeySet.has(childKey));
		if (hasPendingInitialHydration) {
			return;
		}

		const children = orderedChildKeys
			.map((childKey) => childNodeByKey.get(childKey) || null)
			.filter(Boolean);

		const filteredChildren = children.filter((child) =>
			canUser(PermissionAction.VIEW, child, user, parentNode)
		);

		callback(filteredChildren);
	};

	const buildOrderedChildKeys = () => {
		const childKeys = new Set();

		sourceRows.forEach((edge) => {
			if (typeof edge?.targetNodeKey === 'string' && edge.targetNodeKey.trim()) {
				childKeys.add(edge.targetNodeKey.trim());
			}
		});

		targetRows.forEach((edge) => {
			if (typeof edge?.sourceNodeKey === 'string' && edge.sourceNodeKey.trim()) {
				childKeys.add(edge.sourceNodeKey.trim());
			}
		});

		return Array.from(childKeys);
	};

	const syncChildSubscriptions = () => {
		const nextChildKeys = buildOrderedChildKeys();
		const nextChildKeySet = new Set(nextChildKeys);

		childUnsubscribeByKey.forEach((unsubscribe, childKey) => {
			if (nextChildKeySet.has(childKey)) return;
			unsubscribe?.();
			childUnsubscribeByKey.delete(childKey);
			childNodeByKey.delete(childKey);
			hydratedChildKeySet.delete(childKey);
		});

		nextChildKeys.forEach((childKey) => {
			if (childUnsubscribeByKey.has(childKey)) return;

			const unsubscribe = subscribeToNode(childKey, (nextChild) => {
				hydratedChildKeySet.add(childKey);

				if (nextChild) {
					childNodeByKey.set(childKey, nextChild);
				} else {
					childNodeByKey.delete(childKey);
				}

				emitFromCache();
			});

			childUnsubscribeByKey.set(childKey, unsubscribe);
		});

		orderedChildKeys = nextChildKeys;
		emitFromCache();
	};

	const unsubSource = onSnapshot(sourceQuery, (snapshot) => {
		sourceRows = snapshot.docs.map((docSnap) => docSnap.data() || {});
		syncChildSubscriptions();
	}, (error) => {
		console.warn('[NodeReadService] subscribeToChildNodes source edge listener error:', error);
		callback([]);
	});

	const unsubTarget = onSnapshot(targetQuery, (snapshot) => {
		targetRows = snapshot.docs.map((docSnap) => docSnap.data() || {});
		syncChildSubscriptions();
	}, (error) => {
		console.warn('[NodeReadService] subscribeToChildNodes target edge listener error:', error);
		callback([]);
	});

	return () => {
		unsubSource();
		unsubTarget();
		childUnsubscribeByKey.forEach((unsubscribe) => unsubscribe?.());
		childUnsubscribeByKey.clear();
		childNodeByKey.clear();
	};
};

/**
 * Fetches child nodes once and filters them by the viewer's permissions.
 * Mirrors subscribeToChildNodes but resolves a single snapshot for caching flows.
 *
 * @param {string} nodeKey - The node key
 * @param {Object|null} user - Current user object
 * @param {Object|null} parentNode - Parent node object
 * 
 * @returns {Promise<object[]>} - Array of child nodes the user can view.
 */
export const getChildNodesForUser = async (nodeKey, user, parentNode) => {

	// Early return if no nodeKey
	if (!nodeKey) return [];

	const normalizedWorkspaceKey = typeof parentNode?.workspaceKey === 'string' && parentNode.workspaceKey.trim()
		? parentNode.workspaceKey.trim()
		: null;
	const childKeys = await listAdjacentNodeKeys(nodeKey, { workspaceKey: normalizedWorkspaceKey });
	if (childKeys.length === 0) return [];

	const allChildren = await getNodesByKeys(childKeys);

	// Filter based on permissions
	return allChildren.filter((child) =>
		canUser(PermissionAction.VIEW, child, user, parentNode)
	);
};

/**
 * Subscribes to all descendant nodes of a given parent using `path`.
 *
 * @param {string} nodeKey - The node key
 * @param {object} user - Current user object
 * @param {object} parentNode - Parent node object
 * @param {function} callback - Receives an array of descendant nodes
 *
 * @returns {function} unsubscribe
 */
export const subscribeToDescendantNodes = (nodeKey, user, parentNode, callback) => {
	if (!nodeKey || !user || typeof callback !== 'function') {
		return () => {};
	}

	const unsubscribeByNodeKey = new Map();
	const neighborMap = new Map();
	let reachable = new Set([nodeKey]);
	let emitSequence = 0;

	const setsEqual = (left, right) => {
		if (left.size !== right.size) return false;
		for (const value of left) {
			if (!right.has(value)) return false;
		}
		return true;
	};

	const computeReachable = () => {
		const next = new Set([nodeKey]);
		const queue = [nodeKey];

		while (queue.length > 0) {
			const currentKey = queue.shift();
			const neighbors = neighborMap.get(currentKey) || new Set();

			neighbors.forEach((neighborKey) => {
				if (!neighborKey || next.has(neighborKey)) return;
				next.add(neighborKey);
				queue.push(neighborKey);
			});
		}

		return next;
	};

	const emit = async (reachableKeys) => {
		emitSequence += 1;
		const sequence = emitSequence;

		const keys = Array.from(reachableKeys).filter((key) => key !== nodeKey);
		if (keys.length === 0) {
			callback([]);
			return;
		}

		const allDescendants = await getNodesByKeys(keys);
		if (sequence !== emitSequence) return;

		const filteredDescendants = allDescendants.filter((child) =>
			canUser(PermissionAction.VIEW, child, user, parentNode)
		);

		callback(filteredDescendants);
	};

	const subscribeNode = (nodeKey) => {
		if (!nodeKey || unsubscribeByNodeKey.has(nodeKey)) return;

		const normalizedWorkspaceKey = typeof parentNode?.workspaceKey === 'string' && parentNode.workspaceKey.trim()
			? parentNode.workspaceKey.trim()
			: null;

		const unsubscribe = subscribeAdjacentNodeKeys(nodeKey, (neighbors) => {
			const normalizedNeighbors = new Set(
				(Array.isArray(neighbors) ? neighbors : [])
					.filter((key) => typeof key === 'string' && key.trim())
					.map((key) => key.trim())
			);

			neighborMap.set(nodeKey, normalizedNeighbors);

			normalizedNeighbors.forEach((neighborKey) => {
				subscribeNode(neighborKey);
			});

			const nextReachable = computeReachable();
			if (setsEqual(reachable, nextReachable)) return;

			reachable = nextReachable;
			emit(reachable);
		}, { workspaceKey: normalizedWorkspaceKey });

		unsubscribeByNodeKey.set(nodeKey, unsubscribe);
	};

	subscribeNode(nodeKey);
	emit(reachable);

	return () => {
		unsubscribeByNodeKey.forEach((unsubscribe) => unsubscribe?.());
		unsubscribeByNodeKey.clear();
	};
};

/**
 * Fetches descendant nodes once and filters them by the viewer's permissions.
 *
 * @param {string} nodeKey - The node key
 * @param {Object|null} user - Current user object
 * @param {Object|null} parentNode - Parent node object
 *
 * @returns {Promise<object[]>} - Array of descendant nodes the user can view.
 */
export const getDescendantNodesForUser = async (nodeKey, user, parentNode) => {
	if (!nodeKey || !user) return [];
	const visited = new Set([nodeKey]);
	let frontier = [nodeKey];

	while (frontier.length > 0) {
		const normalizedWorkspaceKey = typeof parentNode?.workspaceKey === 'string' && parentNode.workspaceKey.trim()
			? parentNode.workspaceKey.trim()
			: null;
		const neighborLists = await Promise.all(
			frontier.map((currentKey) => listAdjacentNodeKeys(currentKey, { workspaceKey: normalizedWorkspaceKey }))
		);

		const nextFrontier = [];
		neighborLists.forEach((neighbors) => {
			neighbors.forEach((neighborKey) => {
				if (visited.has(neighborKey)) return;
				visited.add(neighborKey);
				nextFrontier.push(neighborKey);
			});
		});

		frontier = nextFrontier;
	}

	visited.delete(nodeKey);
	const allDescendants = await getNodesByKeys(Array.from(visited));

	return allDescendants.filter((child) =>
		canUser(PermissionAction.VIEW, child, user, parentNode)
	);
};

/**
 * Retrieves all nodes authored by the provided user.
 * Sort order is newest first, based on the best available timestamp.
 *
 * @param {string|null} userKey - Author's user key
 * 
 * @returns {Promise<object[]>} - Array of node objects
 */
export const getNodesByAuthor = async (userKey, { visibility = NodeVisibility.ACTIVE } = {}) => {

	// Early return if no userKey
	if (!userKey) return [];

	// Query for nodes by userKey
	const q = query(collection(db, collections.nodes), where('userKey', '==', userKey));

	// Execute query
	const snapshot = await getDocs(q);

	// Map documents to node objects
	const nodes = applyVisibilityToRows(snapshot.docs.map(buildNodeFromDoc), visibility);

	// Sort nodes by dateCreated or dateModified, newest first
	return nodes.slice().sort((a, b) => {
		const aTime = timestampToMillis(a.dateCreated) || timestampToMillis(a.dateModified);
		const bTime = timestampToMillis(b.dateCreated) || timestampToMillis(b.dateModified);
		return bTime - aTime;
	});
};

/**
 * Subscribes to all nodes authored by the provided user and emits sorted rows.
 *
 * @param {string|null} userKey - Author's user key
 * @param {(nodes: object[]) => void} callback - Receives array of node objects
 * 
 * @returns {() => void} unsubscribe
 */
export const subscribeToNodesByAuthor = (userKey, callback, { visibility = NodeVisibility.ACTIVE } = {}) => {

	// Early return if no userKey or invalid callback
	if (!userKey || typeof callback !== 'function') {
		return () => { };
	}

	// Query for nodes by userKey
	const q = query(collection(db, collections.nodes), where('userKey', '==', userKey));

	// Subscribe to query
	return onSnapshot(
		q,
		(snapshot) => {

			// Map documents to node objects
			const nodes = applyVisibilityToRows(snapshot.docs.map(buildNodeFromDoc), visibility);

			// Sort nodes by dateCreated or dateModified, newest first
			nodes.sort((a, b) => {
				const aTime = timestampToMillis(a.dateCreated) || timestampToMillis(a.dateModified);
				const bTime = timestampToMillis(b.dateCreated) || timestampToMillis(b.dateModified);
				return bTime - aTime;
			});

			// Invoke callback with sorted nodes
			callback(nodes);
		},
		(error) => {
			console.error('[NodeReadService] subscribeToNodesByAuthor error:', error);

			// Invoke callback with empty array on error
			callback([]);
		}
	);
};

/**
 * Subscribes to a user's nodes, optionally excluding contributions.
 *
 * Mirrors the legacy `useUserNodes` binding behavior.
 *
 * @param {string|null} userKey - Author's user key
 * @param {(nodes: object[]) => void} callback - Receives array of node objects
 * @param {{ includeContributions?: boolean }} options - Options
 * 
 * @returns {() => void} unsubscribe
 */
export const subscribeToUserNodes = (userKey, callback, { includeContributions = false, visibility = NodeVisibility.ACTIVE } = {}) => {

	// Early return if no userKey or invalid callback
	if (!userKey || typeof callback !== 'function') {
		return () => { };
	}

	// Build query constraints
	const constraints = [where('userKey', '==', userKey)];

	// Create query
	const q = query(collection(db, collections.nodes), ...constraints);

	// Subscribe to query
	return onSnapshot(
		q,
		(snapshot) => {

			// Map documents to node objects
			const nodes = applyVisibilityToRows(snapshot.docs.map(buildNodeFromDoc), visibility);
			const filteredNodes = includeContributions
				? nodes
				: nodes.filter((node) => !(typeof node?.contributorKey === 'string' && node.contributorKey.trim().length > 0));

			// Sort nodes by dateCreated or dateModified, newest first
			filteredNodes.sort((a, b) => {
				const aTime = timestampToMillis(a.dateCreated) || timestampToMillis(a.dateModified);
				const bTime = timestampToMillis(b.dateCreated) || timestampToMillis(b.dateModified);
				return bTime - aTime;
			});

			// Invoke callback with sorted nodes
			callback(filteredNodes);
		},
		(error) => {
			console.error('[NodeReadService] subscribeToUserNodes error:', error);

			// Invoke callback with empty array on error
			callback([]);
		}
	);
};

/**
 * Lists USER profile nodes.
 *
 * @param {{ limitCount?: number, excludeKeys?: string[] }} options - Options
 * 
 * @returns {Promise<object[]>} - Array of user profile nodes
 *
 * Usage:
 * const profiles = await listUserProfiles({ limitCount: 100, excludeKeys: ['admin', 'system'] });
 */
export const listUserProfiles = async ({ limitCount = 50, excludeKeys = [], visibility = NodeVisibility.ACTIVE } = {}) => {

	// Prepare exclusion set
	const excluded = new Set(
		(Array.isArray(excludeKeys) ? excludeKeys : [])
			.filter((k) => typeof k === 'string' && k.trim())
			.map((k) => k.trim())
	);

	// Query for USER type nodes with limit
	const q = query(
		collection(db, collections.nodes),
		where('type', '==', 'USER'),
		limit(limitCount)
	);

	// Execute query
	const snapshot = await getDocs(q);

	// Map documents to node objects, excluding specified keys
	return applyVisibilityToRows(snapshot.docs
		.map(buildNodeFromDoc)
		.filter((row) => row?.key && !excluded.has(row.key)), visibility);
};

/**
 * Fetches node docs by key in batches of 10 (Firestore 'in' clause limit).
 *
 * @param {string[]} keys - Array of node keys
 * 
 * @returns {Promise<object[]>} - Array of node objects
 */
export const listNodesByKeys = async (keys, options = {}) => {
	const { visibility = NodeVisibility.ACTIVE } = options;

	// Validate input
	if (!Array.isArray(keys) || keys.length === 0) return [];

	// Normalize and deduplicate keys
	const uniqueKeys = Array.from(
		new Set(
			keys
				.filter((k) => typeof k === 'string' && k.trim())
				.map((k) => k.trim())
		)
	);

	// Early return if no valid keys
	if (uniqueKeys.length === 0) return [];

	// Split keys into chunks of 10
	const chunks = [];

	// Create chunks of 10 keys each
	for (let i = 0; i < uniqueKeys.length; i += 10) {
		chunks.push(uniqueKeys.slice(i, i + 10));
	}

	// Fetch snapshots for each chunk in parallel
	const snapshots = await Promise.all(
		chunks.map((chunk) => {
			const q = query(
				collection(db, collections.nodes),
				where('__name__', 'in', chunk)
			);
			return getDocs(q);
		})
	);

	// Aggregate results
	const rows = [];

	// Flatten all snapshots into rows
	snapshots.forEach((snapshot) => {
		snapshot.docs.forEach((docSnap) => {
			rows.push(buildNodeFromDoc(docSnap));
		});
	});

	// Preserve requested key order as much as possible
	const order = new Map(uniqueKeys.map((k, idx) => [k, idx]));

	// Sort rows based on original key order
	rows.sort((a, b) => (order.get(a.key) ?? 0) - (order.get(b.key) ?? 0));

	// Return sorted rows
	return applyVisibilityToRows(rows, visibility);
};

/**
 * Subscribes to node docs by key in batches of 10 and emits rows in requested key order.
 *
 * @param {string[]} keys - Array of node keys.
 * @param {(nodes: object[]) => void} callback - Receives readable node rows.
 * @param {{ visibility?: string }} options - Visibility options.
 *
 * @returns {() => void} unsubscribe
 */
export const subscribeToNodesByKeys = (keys = [], callback, options = {}) => {
	const { visibility = NodeVisibility.ACTIVE } = options;

	if (typeof callback !== 'function') {
		return () => {};
	}

	const uniqueKeys = Array.from(
		new Set(
			(Array.isArray(keys) ? keys : [])
				.filter((key) => typeof key === 'string' && key.trim())
				.map((key) => key.trim())
		)
	);

	if (uniqueKeys.length === 0) {
		callback([]);
		return () => {};
	}

	const chunks = [];
	for (let i = 0; i < uniqueKeys.length; i += 10) {
		chunks.push(uniqueKeys.slice(i, i + 10));
	}

	const order = new Map(uniqueKeys.map((key, index) => [key, index]));
	const rowsByKey = new Map();
	const initializedChunkIndexes = new Set();

	const emit = () => {
		if (initializedChunkIndexes.size < chunks.length) return;

		const rows = uniqueKeys
			.map((key) => rowsByKey.get(key) || null)
			.filter(Boolean)
			.sort((left, right) => (order.get(left.key) ?? 0) - (order.get(right.key) ?? 0));

		callback(applyVisibilityToRows(rows, visibility));
	};

	const unsubscribes = chunks.map((chunk, chunkIndex) => {
		const q = query(
			collection(db, collections.nodes),
			where('__name__', 'in', chunk)
		);

		return onSnapshot(
			q,
			(snapshot) => {
				chunk.forEach((key) => {
					rowsByKey.delete(key);
				});

				snapshot.docs.forEach((docSnap) => {
					rowsByKey.set(docSnap.id, buildNodeFromDoc(docSnap));
				});

				initializedChunkIndexes.add(chunkIndex);
				emit();
			},
			(error) => {
				console.error('[NodeReadService] subscribeToNodesByKeys error:', error);
				chunk.forEach((key) => {
					rowsByKey.delete(key);
				});
				initializedChunkIndexes.add(chunkIndex);
				emit();
			}
		);
	});

	return () => {
		unsubscribes.forEach((unsubscribe) => unsubscribe?.());
	};
};

/**
 * Subscribes to nodes using the same (params, sortFields) shape as the legacy subscription pattern.
 * This is a low-level helper for progressively migrating reads into services.
 *
 * @param {{ params?: Array<{field:string, operator:any, value:any}>, sortFields?: Array<string|{field:string, order:'asc'|'desc'}> }} options - Query options
 * @param {(nodes: object[]) => void} callback - Receives array of node objects
 * 
 * @returns {() => void} unsubscribe
 */
export const subscribeToNodes = ({ params = [], sortFields = [], visibility = NodeVisibility.ACTIVE } = {}, callback) => {

	// Early return if invalid callback
	if (typeof callback !== 'function') {
		return () => { };
	}

	// Build query constraints
	const constraints = [];

	// Apply filters
	(params || []).forEach((param) => {
		if (param?.field && param?.operator && param?.value !== undefined) {
			constraints.push(where(param.field, param.operator, param.value));
		}
	});

	// Apply sorting
	(sortFields || []).forEach((field) => {
		if (typeof field === 'string' && field) {
			constraints.push(orderBy(field));
			return;
		}
		if (field?.field && field?.order) {
			constraints.push(orderBy(field.field, field.order));
		}
	});

	// Create query
	const q = query(collection(db, collections.nodes), ...constraints);

	// Subscribe to query
	return onSnapshot(
		q,
		(snapshot) => {

			// Map documents to node objects
			const nodes = applyVisibilityToRows(snapshot.docs.map(buildNodeFromDoc), visibility);

			// Invoke callback with nodes
			callback(nodes);
		},
		(error) => {
			console.error('[NodeReadService] subscribeToNodes error:', error);

			// Invoke callback with empty array on error
			callback([]);
		}
	);
};

/**
 * Subscribes to workspace nodes that reference a specific person in derived peopleKeys.
 *
 * @param {{ workspaceKey?: string, personKey?: string, visibility?: string }} options
 * @param {(nodes: object[]) => void} callback
 *
 * @returns {() => void} unsubscribe
 */
export const subscribeToNodesByPersonKey = ({ workspaceKey, personKey, visibility = NodeVisibility.ACTIVE } = {}, callback) => {
	const normalizedWorkspaceKey = typeof workspaceKey === 'string' ? workspaceKey.trim() : '';
	const normalizedPersonKey = typeof personKey === 'string' ? personKey.trim() : '';

	if (!normalizedWorkspaceKey || !normalizedPersonKey) {
		if (typeof callback === 'function') {
			callback([]);
		}
		return () => {};
	}

	return subscribeToNodes({
		params: [
			{ field: 'workspaceKey', operator: '==', value: normalizedWorkspaceKey },
			{ field: 'peopleKeys', operator: 'array-contains', value: normalizedPersonKey },
		],
		visibility,
	}, callback);
};

/**
 * Builds query params for a collection query, ensuring the type constraint
 * and passing through any future collection filters.
 *
 * @param {{ type?: string, workspaceKey?: string, filters?: Array<object> }} options
 * @returns {Array<{ field: string, operator: any, value: any }>} params
 */
export const buildDatabaseQueryParams = ({ type, workspaceKey } = {}) => {

	const params = [];
	const normalizedWorkspaceKey = String(workspaceKey || '').trim();

	// Constrain by workspace when available.
	if (normalizedWorkspaceKey) {
		params.push({ field: 'workspaceKey', operator: '==', value: normalizedWorkspaceKey });
	}

	// Always constrain by node type
	if (type) {
		params.push({ field: 'type', operator: '==', value: type });
	}

	return params;
};

/**
 * Subscribes to nodes for a collection definition.
 *
 * @param {{ type?: string, workspaceKey?: string, sortFields?: Array<string|{field:string, order:'asc'|'desc'}> }} options
 * @param {(nodes: object[]) => void} callback
 *
 * @returns {() => void} unsubscribe
 */

export const subscribeToNodesByDatabase = ({ type, workspaceKey, sortFields = [], visibility = NodeVisibility.ACTIVE } = {}, callback) => {
	const params = buildDatabaseQueryParams({ type, workspaceKey });
	return subscribeToNodes({ params, sortFields, visibility }, callback);
};

/**
 * Searches nodes by a given search term against the index array.
 * Optionally filters by node type.
 *
 * @param {string} term - The search term (case-insensitive).
 * @param {string} [type] - Optional node type filter (e.g. "USER").
 * 
 * @returns {Promise<object[]>} - Matching nodes.
 *
 * Usage:
 *   const results = await searchNodes("cake"); // all nodes
 *   const profiles = await searchNodes("kevin", "USER"); // only profiles
 */
export const searchNodes = async (term, type, options = {}) => {
	const { visibility = NodeVisibility.ACTIVE } = options;

	// Early return if no search term
	if (!term || term.trim() === "") return [];

	// Normalize search term
	const lowerTerm = term.toLowerCase();

	// Build query constraints
	let constraints = [where("index", "array-contains", lowerTerm)];

	// Filter by type
	if (type) {
		constraints.push(where("type", "==", type));
	}

	// Create query
	const q = query(collection(db, collections.nodes), ...constraints);

	// Execute query
	const snapshot = await getDocs(q);

	// Map documents to node objects
	return applyVisibilityToRows(snapshot.docs.map(buildNodeFromDoc), visibility);
};
